import React, { useEffect, useState } from "react";
import { api, euro } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { HelpTip } from "@/components/HelpTip";
import { AgentPayoutRequest } from "@/components/AgentPayoutRequest";
import { HandCoins, TrendingUp, CheckCircle2, Clock } from "lucide-react";

const eur = (n) => euro(n);

export default function AgentCommissions() {
  const [data, setData] = useState(null);
  const load = () => api.get("/agent/commissions").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-6" data-testid="agent-commissions-page">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2"><HandCoins className="w-4 h-4 text-primary" /><span className="text-xs font-medium tracking-wider uppercase text-muted-foreground">Commerciale</span></div>
          <h1 className="font-head text-3xl font-bold tracking-tight mt-1">Le mie provvigioni</h1>
          <p className="text-sm text-muted-foreground mt-1">Maturazione continua. Il pagamento viene liquidato dall'amministrazione.</p>
        </div>
        <HelpTip cat="provvigioni" />
      </div>

      {!data ? <p className="text-muted-foreground">Caricamento…</p> : (
        <>
          <div className="grid sm:grid-cols-3 gap-4">
            <Card className="p-6 glow-border flex items-center gap-4" data-testid="agent-commissions-total">
              <div className="w-11 h-11 rounded-lg bg-primary/15 text-primary flex items-center justify-center"><TrendingUp className="w-5 h-5" /></div>
              <div><p className="text-xs text-muted-foreground">Totale maturato</p><p className="font-head text-3xl font-bold">{eur(data.total)}</p></div>
            </Card>
            <Card className="p-6 flex items-center gap-4">
              <div className="w-11 h-11 rounded-lg bg-teal-600/15 text-teal-500 flex items-center justify-center"><CheckCircle2 className="w-5 h-5" /></div>
              <div><p className="text-xs text-muted-foreground">Già liquidato</p><p className="font-head text-3xl font-bold text-teal-500">{eur(data.paid_total)}</p></div>
            </Card>
            <Card className="p-6 flex items-center gap-4">
              <div className="w-11 h-11 rounded-lg bg-amber-500/15 text-amber-500 flex items-center justify-center"><Clock className="w-5 h-5" /></div>
              <div><p className="text-xs text-muted-foreground">Da liquidare</p><p className="font-head text-3xl font-bold text-amber-500">{eur(data.outstanding)}</p></div>
            </Card>
          </div>
          <AgentPayoutRequest data={data} onDone={load} />

          <Card className="glow-border">
            <div className="px-5 py-4 border-b border-border"><h2 className="font-head font-semibold">Dettaglio maturato</h2></div>
            <Table>
              <TableHeader><TableRow><TableHead>Voce</TableHead><TableHead>Base</TableHead><TableHead>Tariffa</TableHead><TableHead className="text-right">Maturato</TableHead></TableRow></TableHeader>
              <TableBody>
                {data.rows.map((r) => (
                  <TableRow key={r.key} data-testid={`agent-comm-row-${r.key}`}>
                    <TableCell>{r.label}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{r.count == null ? "—" : (r.is_percent ? eur(r.count) : `${r.count}`)}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{r.unit == null ? "variabile" : (r.is_percent ? `${Number(r.unit).toFixed(0)}%` : `${eur(r.unit)} cad.`)}</TableCell>
                    <TableCell className="text-right font-medium text-teal-500">{eur(r.subtotal)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>

          <Card className="glow-border">
            <div className="px-5 py-4 border-b border-border"><h2 className="font-head font-semibold">Pagamenti liquidati</h2></div>
            <Table>
              <TableHeader><TableRow><TableHead>Data</TableHead><TableHead>Nota</TableHead><TableHead>Stato</TableHead><TableHead className="text-right">Importo</TableHead></TableRow></TableHeader>
              <TableBody>
                {(data.payouts || []).map((p) => (
                  <TableRow key={p.id} data-testid={`agent-payout-${p.id}`}>
                    <TableCell className="text-sm">{p.created_at ? new Date(p.created_at).toLocaleDateString("it-IT") : "—"}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{p.note || "—"}</TableCell>
                    <TableCell><Badge className="bg-teal-600 hover:bg-teal-600">Liquidato</Badge></TableCell>
                    <TableCell className="text-right font-medium">{eur(p.amount)}</TableCell>
                  </TableRow>
                ))}
                {(data.payouts || []).length === 0 && <TableRow><TableCell colSpan={4} className="text-center text-muted-foreground py-6">Nessun pagamento liquidato finora.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </Card>
        </>
      )}
    </div>
  );
}
